import { emptyAdminApi as api } from "../emptyAdminApi";

export type DistributorAuditRecord = {
  DistributorId: string;
  DistributorCode?: string;
  DistributorName?: string;
  EventId: string;
  CreatedAt: string;
  Actor: string;
  ActorSub: string;
  ActorEmail?: string;
  Action: string;
  Outcome: string;
  Source: string;
  Details: Record<string, unknown>;
  Result?: Record<string, unknown>;
  RequestId?: string;
  SessionId?: string;
};

export type DistributorAuditQuery = {
  distributorId: string;
  from?: string;
  to?: string;
  actor?: string;
  action?: string;
  outcome?: string;
  cursor?: string;
};

export type Distributor = {
  id: string;
  code: string;
  name: string;
  status: string;
  walletBalanceCents: number;
  ownerEmail?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateDistributorRequest = {
  code: string;
  name: string;
  ownerEmail: string;
  ownerName?: string;
  temporaryPassword?: string;
  initialBalanceCents?: number;
};

export type UpdateDistributorRequest = Partial<CreateDistributorRequest> & {
  id: string;
  status?: string;
};

export type DistributorTopup = {
  distributorId: string;
  distributorCode?: string;
  distributorName?: string;
  topUpId: string;
  type: string;
  status: string;
  amountCents: number;
  currency: string;
  bankReference?: string;
  confirmationNote?: string;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  requestedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  approvedAt?: string;
  reviewedBy?: string;
  adminNote?: string;
};

export type ReviewTopupRequest = {
  distributorId: string;
  topUpId: string;
  action: "approve" | "reject";
  adminNote?: string;
};

export type DistributorInventorySku = {
  vendorSku: string;
  availableCount: number;
};

export type DistributorInventoryPreviewItem = {
  iccid: string;
  vendorSku: string;
  expiryDate?: string;
  createdAt?: string;
};

export type DistributorInventoryPreview = {
  vendorSku: string;
  availableCount: number;
  preview: DistributorInventoryPreviewItem[];
};

export type AssignDistributorInventoryRequest = {
  distributorId: string;
  vendorSku: string;
  iccids: string[];
};

export type AssignDistributorInventoryResponse = {
  assignedCount: number;
  distributorId: string;
  distributorCode: string;
  vendorSku: string;
  inventory: Array<{ iccid: string; vendorSku: string }>;
};

export type DistributorInventoryUpload = {
  objectId: string;
  uploadUrl: string;
  fileUrl: string;
};

export const distributorAdminApi = api
  .enhanceEndpoints({
    addTagTypes: ["Distributor", "DistributorTopup", "DistributorInventory"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getDistributorAudit: build.query<{
        records: DistributorAuditRecord[];
        cursor: string | null;
      }, DistributorAuditQuery>({
        query: (params) => ({
          url: "/portal/distributor-audit",
          params,
        }),
      }),
      getDistributors: build.query<Distributor[], void>({
        query: () => "/portal/distributors",
        transformResponse: (response: { distributors: Distributor[] }) => response.distributors,
        providesTags: ["Distributor"],
      }),
      createDistributor: build.mutation<Distributor, CreateDistributorRequest>({
        query: (body) => ({
          url: "/portal/distributors",
          method: "POST",
          body,
        }),
        transformResponse: (response: { distributor: Distributor }) => response.distributor,
        invalidatesTags: ["Distributor"],
      }),
      updateDistributor: build.mutation<Distributor, UpdateDistributorRequest>({
        query: (body) => ({
          url: "/portal/distributors",
          method: "PUT",
          body,
        }),
        transformResponse: (response: { distributor: Distributor }) => response.distributor,
        invalidatesTags: ["Distributor"],
      }),
      disableDistributor: build.mutation<{ id: string; status: string }, string>({
        query: (id) => ({
          url: `/portal/distributors?id=${encodeURIComponent(id)}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Distributor"],
      }),
      getDistributorTopups: build.query<DistributorTopup[], string | void>({
        query: (status) => status ? `/portal/distributor-topups?status=${encodeURIComponent(status)}` : "/portal/distributor-topups",
        transformResponse: (response: { topups: DistributorTopup[] }) => response.topups,
        providesTags: ["DistributorTopup"],
      }),
      reviewDistributorTopup: build.mutation<DistributorTopup, ReviewTopupRequest>({
        query: (body) => ({
          url: "/portal/distributor-topups",
          method: "PUT",
          body,
        }),
        transformResponse: (response: { topup: DistributorTopup }) => response.topup,
        invalidatesTags: ["Distributor", "DistributorTopup"],
      }),
      getDistributorInventorySummary: build.query<{ skus: DistributorInventorySku[] }, void>({
        query: () => "/portal/distributor-inventory",
        providesTags: ["DistributorInventory"],
      }),
      getDistributorInventoryPreview: build.query<DistributorInventoryPreview, { vendorSku: string; quantity: number }>({
        query: ({ vendorSku, quantity }) =>
          `/portal/distributor-inventory?vendorSku=${encodeURIComponent(vendorSku)}&quantity=${quantity}`,
        providesTags: ["DistributorInventory"],
      }),
      assignDistributorInventory: build.mutation<AssignDistributorInventoryResponse, AssignDistributorInventoryRequest>({
        query: (body) => ({
          url: "/portal/distributor-inventory",
          method: "POST",
          body,
        }),
        invalidatesTags: ["DistributorInventory"],
      }),
      createDistributorInventoryUpload: build.mutation<DistributorInventoryUpload, { filename: string }>({
        query: (body) => ({
          url: "/inventory/upload?key=input",
          method: "POST",
          body,
        }),
      }),
    }),
  });

export const {
  useGetDistributorAuditQuery,
  useCreateDistributorMutation,
  useDisableDistributorMutation,
  useGetDistributorTopupsQuery,
  useGetDistributorsQuery,
  useGetDistributorInventorySummaryQuery,
  useLazyGetDistributorInventoryPreviewQuery,
  useAssignDistributorInventoryMutation,
  useCreateDistributorInventoryUploadMutation,
  useReviewDistributorTopupMutation,
  useUpdateDistributorMutation,
} = distributorAdminApi;
